import React from "react"
import { Slot } from "@radix-ui/react-slot"

import { TResolvedImage } from "@/types/kontent.types"
import { getSyncDictionary } from "@/lib/sync-dictionary"
import {
  cn,
  ensureHttpUrl,
  formatEventDate,
  formatEventTime,
} from "@/lib/utils"
import { Link, Tag, Typography } from "@/components/ui/atoms"

import { Picture } from "../../picture/Picture"

interface ICardEventProps {
  id?: string
  asChild?: string
  className?: string
  title: string
  description: string
  location: string
  siteUrl: string
  canton: string
  image: TResolvedImage
  startDate: Date
  endDate?: Date
  showStartHour?: boolean
  showEndHour?: boolean
  tagLabel?: string
}

const CardEvent = React.forwardRef<HTMLDivElement, ICardEventProps>(
  (
    {
      asChild = false,
      className,
      image,
      title,
      description,
      tagLabel,
      location,
      canton,
      siteUrl,
      showStartHour,
      showEndHour,
      startDate,
      endDate,
      ...props
    },
    ref
  ) => {
    const Component = asChild ? Slot : "div"

    const formattedWebsite = ensureHttpUrl(siteUrl)

    return (
      <Component
        className={cn(
          "card-event mx-auto w-full max-w-[1000px] bg-neutral-200 md:flex",
          className
        )}
        ref={ref}
        {...props}
      >
        <div>
          <Picture
            src={image.src + "?h=400&w=400&fm=webp&q=80"}
            alt={image.alt ? image.alt : ""}
            width={200}
            height={200}
            isRounded={true}
            variant={"default"}
            className="hidden max-h-[225px] min-h-[200px] w-[200px] shrink-0  md:flex"
          />
          <Picture
            src={image.src + "?h=3200&w=900&fm=webp&q=80"}
            alt={image.alt ? image.alt : ""}
            width={800}
            height={225}
            isRounded={true}
            variant={"default"}
            tagLabel={tagLabel}
            alignLeft={true}
            className=" shrink-0 overflow-hidden rounded-100 md:hidden"
          />
        </div>
        <div className="w-full pt-6 md:pl-6 md:pt-0">
          <div className="mb-5 items-start justify-between md:flex">
            <div className="mb-5 md:mb-0">
              <Typography
                as="h3"
                className="font-500 text-neutral-400"
                variant="headingM"
              >
                {formatEventDate(startDate)}
                {showStartHour ? (
                  <span className="label-m text-neutral-400">
                    <span className="px-3">·</span>
                    {formatEventTime(startDate)}
                  </span>
                ) : null}
                {endDate ? " - " + formatEventDate(endDate) : null}
                {endDate && showEndHour ? (
                  <span className="label-m text-neutral-400">
                    <span className="px-3">·</span>
                    {formatEventTime(endDate)}
                  </span>
                ) : null}
              </Typography>
            </div>
            <div className="mb-5 flex items-center md:mb-0">
              {tagLabel ? (
                <Tag
                  variant="accent"
                  className="mr-5 hidden md:block"
                  label={tagLabel}
                />
              ) : null}
              <Tag variant="light" icon="Pin" label={location} />
            </div>
          </div>
          <div>
            <Typography as="h2" className="pb-5" variant="headingS">
              {title}
            </Typography>
            <Typography
              as="p"
              variant="paragraphS"
              className="line-clamp-3 text-neutral-400"
            >
              {description}
            </Typography>
          </div>
          {formattedWebsite ? (
            <div className="mt-5 flex justify-end">
              <Link
                chapters={[title, getSyncDictionary().page.evenements.site]}
                variant="ghost"
                iconRight="ExternalLinkLine"
                className="p-0"
                href={formattedWebsite}
                target={"_blank"}
              >
                {getSyncDictionary().page.evenements.site}
              </Link>
            </div>
          ) : null}
        </div>
      </Component>
    )
  }
)

export { CardEvent, type ICardEventProps }
